import React, { useState } from "react";
import { X } from "lucide-react";
import api from "../../../../utils/api";
import { EmailChips, Sub, uniq, isPlaceholder } from "./ui";

// Default To / CC per team for "Send to next in line". Any address used in a hand-off is added automatically.
export default function ContactsPanel({ teams, contacts, onClose, onSaved }) {
    const [draft, setDraft] = useState(contacts);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const all = uniq(Object.values(contacts).flatMap(x => [...x.to, ...x.cc]));
    const placeholders = uniq(Object.values(draft).flatMap(x => [...x.to, ...x.cc]).filter(isPlaceholder));

    const save = async () => {
        setBusy(true); setErr("");
        try {
            const res = await api.put("/api/excel-ingestion/pallet-contacts", draft);
            onSaved(res.data);
            onClose();
        } catch (e) {
            setErr(e.response?.data?.error || "Could not save the contacts");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="fixed inset-0 flex items-start justify-center p-4 overflow-y-auto" style={{ background: "rgba(0,0,0,0.80)", zIndex: 9999 }}
            onMouseDown={e => e.target === e.currentTarget && onClose()}>
            <div role="dialog" aria-modal="true" aria-labelledby="ct-h" className="bg-[#111827] border border-[#1F2937] rounded-2xl w-full max-w-3xl my-4 shadow-2xl">
                <header className="flex items-start justify-between gap-4 px-6 py-4 border-b border-[#1F2937]">
                    <div>
                        <h2 id="ct-h" className="text-base font-bold text-white">Notification contacts</h2>
                        <Sub>Default To and CC per team for “Send to next in line”. Any address you send to is added here.</Sub>
                    </div>
                    <button onClick={onClose} aria-label="Close" className="w-8 h-8 rounded-lg bg-[#1F2937] flex items-center justify-center text-slate-400 hover:text-white"><X size={15}/></button>
                </header>
                <div className="p-6 space-y-4">
                    {placeholders.length > 0 && (
                        <div className="bg-amber-900/20 border border-amber-700/50 rounded-lg px-3 py-2 text-[11px] text-amber-300">
                            {placeholders.length} address{placeholders.length !== 1 ? "es are" : " is"} still an example.com placeholder (highlighted). Replace them with real team addresses.
                        </div>
                    )}
                    {teams.map(t => (
                        <div key={t} className="grid grid-cols-1 md:grid-cols-[160px_1fr_1fr] gap-3 items-start border-b border-[#1F2937] pb-3">
                            <h4 className="text-[12px] font-bold text-slate-200 pt-5">{t}</h4>
                            <EmailChips id={`c-to-${t}`} label="To" value={draft[t]?.to || []} suggestions={all} placeholder="name@company.com"
                                onChange={v => setDraft({ ...draft, [t]: { ...(draft[t] || { cc: [] }), to: v } })}/>
                            <EmailChips id={`c-cc-${t}`} label="CC" value={draft[t]?.cc || []} suggestions={all} placeholder="Optional"
                                onChange={v => setDraft({ ...draft, [t]: { ...(draft[t] || { to: [] }), cc: v } })}/>
                        </div>
                    ))}
                    {err && <p className="text-[11px] text-red-400" role="alert">{err}</p>}
                    <div className="flex justify-end gap-2">
                        <button onClick={onClose} className="px-3 py-1.5 rounded-lg border border-[#374151] text-[12px] text-slate-300 hover:border-slate-500">Cancel</button>
                        <button onClick={save} disabled={busy} className="px-3 py-1.5 rounded-lg bg-blue-600 text-[12px] font-semibold text-white hover:bg-blue-500 disabled:opacity-50">
                            {busy ? "Saving…" : "Save contacts"}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
